// Use for a short message inside a page section (info, success, warning,
// danger): a tinted strip with an icon, the text in the primary colour (the
// tint carries the tone, so the words stay readable in every theme). For a
// whole empty area use EmptyState; for a failed load, LoadError.

import type { ReactNode } from "react";
import { AlertTriangle, CircleCheck, Info, XCircle } from "lucide-react";

import { cn } from "@/lib/utils";

type Tone = "info" | "success" | "warning" | "danger";

const STYLE: Record<Tone, string> = {
  info: "bg-info/10 shadow-[inset_0_0_0_1px_rgb(var(--tw-info)/0.3)] [&_[data-icon]]:text-info",
  success: "bg-success/10 shadow-[inset_0_0_0_1px_rgb(var(--tw-success)/0.3)] [&_[data-icon]]:text-success",
  warning: "bg-warning/10 shadow-[inset_0_0_0_1px_rgb(var(--tw-warning)/0.3)] [&_[data-icon]]:text-warning",
  danger: "bg-danger/10 shadow-[inset_0_0_0_1px_rgb(var(--tw-danger)/0.3)] [&_[data-icon]]:text-danger",
};

const ICON: Record<Tone, typeof Info> = {
  info: Info,
  success: CircleCheck,
  warning: AlertTriangle,
  danger: XCircle,
};

export function Notice({
  tone = "info",
  children,
  action,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const Icon = ICON[tone];
  return (
    <div
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      className={cn(
        "flex flex-wrap items-start gap-x-2.5 gap-y-2 rounded-control px-3 py-2.5 text-sm text-fg",
        STYLE[tone],
        className,
      )}
    >
      <Icon data-icon="" aria-hidden className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      {/* The action drops under the text when the strip is narrow (phones). */}
      <div className="min-w-[12rem] flex-1 basis-0">{children}</div>
      {action && <div className="-my-1 ml-auto shrink-0">{action}</div>}
    </div>
  );
}
